import { ChatCircle } from "@/components/ui/icons";
import { Button } from "@/components/ui/button";
import { ThinkingSection } from "@/components/ui/thinking-section";
import { AnimatedImage } from "@/components/ui/animated-image";
import { LinkPreview } from "../LinkPreview";
import { AttachmentPreview } from "../AttachmentPreview";
import { renderMessageContent } from "../message-cards";

interface MessageBodyProps {
  message: {
    id: string;
    content: string;
    kind?: string | null;
    payload?: Record<string, unknown> | null;
    is_broadcast?: boolean;
    thread_id?: string | null;
    /** mig 130 — citation FK to source message; non-null = inline reply. */
    replied_to_message_id?: string | null;
    metadata?: { thinking?: string } | null;
  };
  /** mig 130 — when this row is an inline reply, the source's row data
   *  (resolved from the parent's `messageById` map). Drives the quote-pill
   *  header. Null when source isn't in the local map (paginated out or
   *  deleted) — pill renders without a snippet in that case. */
  quotedSource?: {
    id: string;
    content: string;
    user_id: string;
  } | null;
  /** Display name of the quoted source's author, if known. */
  quotedSourceAuthorName?: string | null;
  /** Avatar URL of the quoted source's author, if known. Drives the
   *  16px avatar in the quote-pill (Discord pattern). */
  quotedSourceAuthorAvatarURL?: string | null;
  isOwn: boolean;
  mentionableUsers: { id: string; display_name: string }[];
  onUserClick?: (userId: string) => void;
  channels?: { id: string; name: string }[];
  onChannelClick?: (channelId: string) => void;
  attachmentList: readonly {
    id: string;
    message_id: string;
    user_id: string;
    filename: string;
    file_type: string;
    file_size: number;
    file_category: string;
    storage_key: string;
    storage_url: string;
    thumbnail_url?: string | null;
    width?: number | null;
    height?: number | null;
    created_at: number;
  }[];
  linkPreviewList: readonly {
    id: string;
    message_id: string;
    url: string;
    title?: string | null;
    description?: string | null;
    image_url?: string | null;
    favicon_url?: string | null;
    site_name?: string | null;
    fetched_at: number;
    created_at: number;
  }[];
  onThreadClick?: (messageId: string) => void;
  /** Inline-reply citation pill click — scrolls the channel timeline
   *  to the source message in-place. Discord-shape: replies live in
   *  the channel, so the citation jumps the reader to the cited row,
   *  not into a thread sidebar. */
  onScrollToMessage?: (messageId: string) => void;
}

/**
 * Body block under the header: optional thinking-section chip, message
 * content (kind-dispatched via `renderMessageContent`), broadcast pill
 * (link to thread), attachments, link previews.
 *
 * Coworker streaming UX (thinking chip + progressive content) is rendered
 * by `<StreamingDmBubble>` in `MessageArea`, driven by SSE events into
 * `agentDmStreamStore`. The in-flight bubble disappears when the SSE
 * finishes; the real DB row (this component) takes over and renders the
 * final content + the persisted "Thought process" chip (when
 * metadata.thinking is set).
 */
export function MessageBody({
  message,
  quotedSource,
  quotedSourceAuthorName,
  quotedSourceAuthorAvatarURL,
  isOwn,
  mentionableUsers,
  onUserClick,
  channels,
  onChannelClick,
  attachmentList,
  linkPreviewList,
  onThreadClick,
  onScrollToMessage,
}: MessageBodyProps) {
  const isInlineReply = message.replied_to_message_id != null;
  return (
    <>
      {/* WhatsApp-shape inline-reply citation: 3px left accent bar +
          stacked rows (avatar + name on top, snippet below). Click
          scrolls the channel timeline to the source row in-place.
          Renders "[message]" fallback when the source isn't in the
          local map (pagination cutoff / deleted FK ON DELETE SET NULL).
          Avatar before name matches the natural reading order
          (face → name → content) used by WhatsApp / iMessage / Telegram.
          Attachment-hint line ("📷 2 photos") is a follow-up — needs
          attachment data threaded through `quotedSource`. */}
      {isInlineReply && (
        <Button
          type="button"
          variant="ghost"
          aria-label={`Jump to message from ${quotedSourceAuthorName ?? "unknown sender"}`}
          onClick={() =>
            onScrollToMessage?.(message.replied_to_message_id as string)
          }
          onMouseEnter={() => {
            const id = message.replied_to_message_id;
            if (!id) return;
            document
              .querySelector(`[data-message-id="${CSS.escape(id)}"]`)
              ?.classList.add("cite-target");
          }}
          onMouseLeave={() => {
            const id = message.replied_to_message_id;
            if (!id) return;
            document
              .querySelector(`[data-message-id="${CSS.escape(id)}"]`)
              ?.classList.remove("cite-target");
          }}
          className="group/cite mb-1.5 flex h-auto max-w-full flex-col gap-0 rounded-r-md border-l-[3px] border-info/70 py-0 pl-2 pr-2 text-left hover:border-info hover:bg-info/[0.04]"
        >
          <div className="flex items-center gap-1.5 text-[11px] font-medium text-info">
            {quotedSourceAuthorAvatarURL ? (
              <span className="h-3.5 w-3.5 rounded-full overflow-hidden bg-foreground/20 shrink-0 flex items-center justify-center">
                <AnimatedImage
                  src={quotedSourceAuthorAvatarURL}
                  alt={quotedSourceAuthorName ?? ""}
                  className="h-full w-full object-cover"
                />
              </span>
            ) : null}
            <span className="truncate">
              {quotedSourceAuthorName ?? "unknown"}
            </span>
          </div>
          <span className="text-[11px] text-muted-foreground group-hover/cite:text-foreground/80 transition-colors line-clamp-3 whitespace-pre-wrap break-words">
            {quotedSource
              ? makeQuoteSnippet(quotedSource.content)
              : "[message]"}
          </span>
        </Button>
      )}
      {message.metadata?.thinking ? (
        <ThinkingSection text={message.metadata.thinking} isStreaming={false} />
      ) : null}
      {message.content || message.kind !== "text"
        ? renderMessageContent(
            {
              id: message.id,
              content: message.content,
              kind: message.kind ?? "text",
              payload: (message.payload ?? null) as Record<
                string,
                unknown
              > | null,
              canEdit: isOwn,
            },
            mentionableUsers,
            onUserClick,
            channels,
            onChannelClick,
          )
        : null}
      {message.is_broadcast && message.thread_id && onThreadClick && (
        <Button
          type="button"
          variant="link"
          onClick={() => onThreadClick(message.thread_id!)}
          className="mt-1 h-auto gap-1 p-0 text-[11px] text-muted-foreground"
        >
          <ChatCircle size={12} />
          <span>Replied in thread</span>
        </Button>
      )}
      {attachmentList.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {attachmentList.map((attachment) => (
            <AttachmentPreview key={attachment.id} attachment={attachment} />
          ))}
        </div>
      )}
      {linkPreviewList.length > 0 && (
        <div className="flex flex-col">
          {linkPreviewList.map((preview) => (
            <LinkPreview key={preview.id} preview={preview} />
          ))}
        </div>
      )}
    </>
  );
}

/** Single-line, length-capped excerpt of the source content for the quote-pill. */
function makeQuoteSnippet(content: string): string {
  const collapsed = content.replace(/\s+/g, " ").trim();
  if (!collapsed) return "[empty]";
  if (collapsed.length <= 80) return collapsed;
  return collapsed.slice(0, 79).trimEnd() + "…";
}
